import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { TAutoModel } from 'shared/types';

import { mapDictionary } from '../lib/mapDictionary';
import type { PledgeAutoFormEntityReducer, TPledgeAutoFormIncomingDictionaries, TPledgeAutoFields } from '../types';

import { queries } from './pledgeAutoForm.query';
import { PledgeAutoValidationSchema } from './pledgeAutoForm.validationSchema';

// Выбираемые значения должны быть null, чтобы при стирании они менялись на undefined и становились dirty
const initialState: PledgeAutoFormEntityReducer = {
  data: null,
  defaults: {
    carBrand: null,
    carManufactureYear: null,
    carModel: null,
    carNumber: '',
    enginePower: null,
    isTaxi: false,
    isOwner: true,
  },
  dictionaries: {
    brands: [],
    models: {},
    years: {},
    powers: {},
  },
  isFullFilled: false,
  isActive: true,
  isOpenedInPopup: false,
  carInfoStatus: 'initial',
  vehicleType: 'car',
};

export const pledgeAutoFormSlice = createSlice({
  name: 'pledgeAutoForm',
  initialState,
  reducers: {
    setPledgeAutoFormDictionaries: (
      state,
      { payload }: PayloadAction<Partial<TPledgeAutoFormIncomingDictionaries>>,
    ) => {
      const { brands, models, powers, years } = mapDictionary(payload);

      return {
        ...state,
        dictionaries: {
          brands: brands?.length ? brands : state.dictionaries.brands,
          models: {
            ...state.dictionaries.models,
            ...models,
          },
          powers: {
            ...state.dictionaries.powers,
            ...powers,
          },
          years: {
            ...state.dictionaries.years,
            ...years,
          },
        },
      };
    },
    setPledgeAutoInfo: (state, { payload }: PayloadAction<{ data: TPledgeAutoFields; isFullFilled?: boolean }>) => {
      state.data = { ...payload.data, isTaxi: state.defaults?.isTaxi || payload.data.isTaxi };
      state.defaults = { ...state.defaults, isTaxi: state.defaults?.isTaxi || payload.data.isTaxi };
      state.isFullFilled =
        payload.isFullFilled !== undefined
          ? payload.isFullFilled
          : PledgeAutoValidationSchema().isValidSync(payload.data);
    },

    setPledgeAutoInfoDefault: (state, { payload }: PayloadAction<Partial<TPledgeAutoFields>>) => {
      state.defaults = {
        ...initialState.defaults,
        ...payload,
        isTaxi: state.defaults?.isTaxi || payload.isTaxi,
      };
      state.isFullFilled = false;
    },
    setPledgeAutoInfoValidData: (state, { payload }: PayloadAction<Partial<TPledgeAutoFields>>) => {
      state.data = { ...state.data, ...(payload as TPledgeAutoFields) };
    },
    setCarInfoStatus: (state, { payload }: PayloadAction<PledgeAutoFormEntityReducer['carInfoStatus']>) => {
      state.carInfoStatus = payload;
    },

    resetCarInfo: (state) => {
      state.defaults = { ...initialState.defaults, isTaxi: state.defaults.isTaxi || state.data?.isTaxi };
      state.data = initialState.data;
      state.isFullFilled = false;
      state.carInfoStatus = initialState.carInfoStatus;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      queries.endpoints.getEnginePowers.matchFulfilled,
      (state, { payload }: PayloadAction<Dictionaries.TObjectStore<number, Dictionaries.TSelectOption>>) => ({
        ...state,
        dictionaries: {
          ...state.dictionaries,
          powers: {
            ...state.dictionaries.powers,
            ...payload,
          },
        },
      }),
    );

    builder.addMatcher(
      queries.endpoints.getModels.matchFulfilled,
      (state, { payload }: PayloadAction<Dictionaries.TObjectStore<number | string, TAutoModel>>) => ({
        ...state,
        dictionaries: {
          ...state.dictionaries,
          models: {
            ...state.dictionaries.models,
            ...payload,
          },
        },
      }),
    );

    builder.addMatcher(
      queries.endpoints.getManufactureYears.matchFulfilled,
      (state, { payload }: PayloadAction<Dictionaries.TObjectStore<number, Dictionaries.TSelectOption>>) => ({
        ...state,
        dictionaries: {
          ...state.dictionaries,
          years: {
            ...state.dictionaries.years,
            ...payload,
          },
        },
      }),
    );
  },
});

export const { setPledgeAutoFormDictionaries, setPledgeAutoInfo, setCarInfoStatus, setPledgeAutoInfoValidData } =
  pledgeAutoFormSlice.actions;
